import Cookies from 'js-cookie';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetMenu, usePostLogout } from '../../api/auth/auth';
import { User } from '../../api/model/user';
import { useGetNotifications } from '../../api/notifications/notifications';
import { COOKIE_NAME_FOR_API_KEY } from '../../constants/api';
import { NUM_NOTIFICATIONS_THRESHOLD } from '../../constants/notification';
import { useDefaultOptions } from '../../hooks/api';
import { getPageType } from '../../utils/pathname';
import * as SessionStorageUtils from '../../utils/sessionStorage';
import Link from '../atoms/Link';
import { useEditModeValue } from '../layouts/LoginedLayout';
import ConfirmModal from './ConfirmModal';
import ErrorModal from './ErrorModal';
import NotificationsModal from './NotificationsModal';
import SpinnerModal from './SpinnerModal';

interface Props {
  loginUserInfo: User;
  dispHeaderNavi: boolean;
}

/**
 * 共通ヘッダー
 * @param param0
 * @returns
 */
function Header({ loginUserInfo, dispHeaderNavi }: Props) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // データの取得
  const options = useDefaultOptions(language);

  // メニューの権限
  const { data: authData } = useGetMenu(options);

  // URL のうちのパス部分を取得
  const { pathname } = useLocation();

  // 現在のページに関するパンくずリストの情報を取得
  const pageInfo = useMemo(() => {
    // URL のパスから言語を除いたもの
    const pageType = getPageType(pathname);

    // URL とパンくずリストの関係性
    const pageInfos = [
      {
        regexp: /^home$/,
        faClassName: 'fas fa-home',
        breadcrumbs: [t('Home')],
      },
      {
        regexp: /^productions\/$/,
        faClassName: 'fa-solid fa-list-check',
        breadcrumbs: [t('制作一覧')],
      },
      {
        regexp: /^productions\/add$/,
        faClassName: 'fa-solid fa-list-check',
        breadcrumbs: [t('制作一覧'), t('制作管理')],
      },
      {
        regexp: /^productions\/edit\/\d+$/,
        faClassName: 'fa-solid fa-list-check',
        breadcrumbs: [t('制作一覧'), t('制作管理')],
      },
      {
        regexp: /^schedulelist$/,
        faClassName: 'fa-solid fa-calendar-check',
        breadcrumbs: [t('日程一覧')],
      },
      {
        regexp: /^ps#?\d?$/,
        faClassName: 'fa-solid fa-book',
        breadcrumbs: [t('PS一覧')],
      },
      {
        regexp: /^ps\/view\/\d+\/psnote$/,
        faClassName: 'fa-solid fa-book',
        breadcrumbs: [t('PS一覧'), t('PS閲覧'), t('PS改廃案メモ一覧')],
      },
      {
        regexp: /^ps\/view\b/,
        faClassName: 'fa-solid fa-book',
        breadcrumbs: [t('PS一覧'), t('PS閲覧')],
      },
      {
        regexp: /^ps\/add\b/,
        faClassName: 'fa-solid fa-book',
        breadcrumbs: [t('PS一覧'), t('PS追加')],
      },
      {
        regexp: /^ps\/edit\b/,
        faClassName: 'fa-solid fa-book',
        breadcrumbs: [t('PS一覧'), t('PS編集')],
      },
      {
        regexp: /^ps\/diff\/-1\/\b/,
        faClassName: 'fa-solid fa-book',
        breadcrumbs: [t('PS一覧'), t('PS比較 - 追加PS')],
      },
      {
        regexp: /^ps\/diff\/\d+\/-1$/,
        faClassName: 'fa-solid fa-book',
        breadcrumbs: [t('PS一覧'), t('PS比較 - 削除PS')],
      },
      {
        regexp: /^ps\/diff\b/,
        faClassName: 'fa-solid fa-book',
        breadcrumbs: [t('PS一覧'), t('PS比較')],
      },
    ];

    // 現在の URL に関する情報を返す
    for (let i = 0; i < pageInfos.length; i += 1) {
      if (pageInfos[i].regexp.test(pageType)) {
        return pageInfos[i];
      }
    }
    return undefined;
  }, [t, pathname]);

  // 通知は設定された時間（ミリ秒）ごとにリフェッチする
  const defaultOptions = useDefaultOptions(
    language,
    parseInt(process.env.REACT_APP_REFETCH_NOTIFICATIONS_INTERVAL ?? '90000', 10)
  );
  const notifications = useGetNotifications(defaultOptions);
  const [openNotificationsModal, setOpenNotificationsModal] = useState(false);

  // ページ遷移するためのもの
  const navigate = useNavigate();

  // ログアウトモーダルを表示するかどうか
  const [displayLogoutModal, setDisplayLogoutModal] = useState(false);

  // スピナーモーダルを表示するかどうか
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

  // ログアウトボタンがクリックされた場合の処理
  const { mutateAsync: logoutAsync, error: logoutError } = usePostLogout(defaultOptions);

  // ログアウトエラーを表示するかどうか
  const [displayLogoutError, setDisplayLogoutError] = useState(false);

  // 編集モードかどうか
  const editMode = useEditModeValue();

  // ログアウトボタンクリック時の動作
  const onClickLogout = useCallback(async () => {
    // eslint-disable-next-line no-alert
    if (editMode && !window.confirm(t('入力されたデータは保存されていませんが、よろしいですか？').toString())) {
      return;
    }
    setDisplaySpinnerModal(true);
    await logoutAsync(undefined, {
      onSuccess: () => {
        Cookies.remove(COOKIE_NAME_FOR_API_KEY);
        SessionStorageUtils.clear();
        navigate(`/${language}/login?logout=1`);
      },
      onError: (error) => {
        // 他のタブなどでログアウトしていた場合はエラー表示しないでログアウト
        if (error?.response?.data.error_code === 'ApiAuthNotLoggedIn') {
          SessionStorageUtils.clear();
          navigate(`/${language}/login?logout=1`);
          return;
        }

        // エラー発生時
        setDisplaySpinnerModal(false);
        setDisplayLogoutModal(false);
        setDisplayLogoutError(true);
      },
    });
  }, [navigate, language, logoutAsync, t, editMode]);

  return (
    <>
      <header>
        <div id="header-in">
          {/* CMS-POC のロゴを表示 */}
          <div id="sign">
            <h1>
              <img src="/images/cms_logo.png" style={{ height: '43px' }} alt="CMS-POC" />
            </h1>
          </div>
          {/* パンくずリストを表示 */}
          <div id="topicpath">
            <ul>
              {pageInfo != null &&
                pageInfo.breadcrumbs.map((x, i) =>
                  i === 0 ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={i}>
                      <i className={pageInfo?.faClassName} />
                      {x}
                    </li>
                  ) : (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={i} className="tpath">
                      <i className="fa-solid fa-chevron-right" />
                      {x}
                    </li>
                  )
                )}
            </ul>
          </div>
          {dispHeaderNavi && (
            <div id="headernavi">
              <ul>
                {/* 通知件数を表示 */}
                <li className="bell-i modal-open">
                  <button
                    type="submit"
                    className="text-button"
                    onClick={async () => {
                      setDisplaySpinnerModal(true);
                      await notifications.refetch();
                      setDisplaySpinnerModal(false);
                      setOpenNotificationsModal(true);
                    }}
                  >
                    <i className="fas fa-bell" />
                    {!notifications.isLoading &&
                      notifications.error == null &&
                      notifications.data != null &&
                      notifications.data.data.notifications != null &&
                      notifications.data.data.notifications.length > 0 && (
                        <span>
                          {notifications.data.data.notifications.length > NUM_NOTIFICATIONS_THRESHOLD
                            ? `${NUM_NOTIFICATIONS_THRESHOLD}+`
                            : notifications.data.data.notifications.length}
                        </span>
                      )}
                  </button>
                </li>
                {/* ユーザーID を表示 */}
                <li className="user-i">
                  {/* <Link to={loginUserInfo.user_id != null ? `/${language}/users/edit/${loginUserInfo.user_id}` : '.'}> */}
                  <Link
                    to={loginUserInfo.user_id != null ? `/${language}/users/edit/${loginUserInfo.user_id}` : '.'}
                    state={{ transitionSource: 'home' }}
                    className={authData?.data.user_edit === false ? 'disabled-link' : undefined}
                  >
                    <i className="fa-solid fa-user" />
                    {language === 'ja' ? loginUserInfo.user_name_ja : loginUserInfo.user_name_en}
                  </Link>
                </li>
                {/* 戻るボタンを表示 */}
                <li className="return-i">
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => {
                      if (
                        editMode &&
                        // eslint-disable-next-line no-alert
                        !window.confirm(t('入力されたデータは保存されていませんが、よろしいですか？').toString())
                      ) {
                        return;
                      }
                      navigate(-1);
                    }}
                  >
                    <i className="fa-solid fa-arrow-left" />
                    {t('戻る')}
                  </button>
                </li>
                {/* ログアウトボタンを表示 */}
                <li className="logout-i">
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => {
                      setDisplayLogoutModal(true);
                    }}
                  >
                    <i className="fa-solid fa-arrow-right-from-bracket" />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </header>
      {!notifications.isLoading &&
        notifications.error == null &&
        notifications.data != null &&
        notifications.data.data.notifications != null && (
          <NotificationsModal
            active={openNotificationsModal}
            notifications={notifications.data.data.notifications}
            onClose={() => setOpenNotificationsModal(false)}
            refech={async () => {
              await notifications.refetch();
            }}
          />
        )}
      {/* ログアウト確認モーダル */}
      {displayLogoutModal && (
        <ConfirmModal onClickCancel={() => setDisplayLogoutModal(false)} onClickOk={onClickLogout}>
          <div>{t('ログアウトしますか？')}</div>
        </ConfirmModal>
      )}
      {/* スピナーモーダル */}
      {displaySpinnerModal && <SpinnerModal />}
      {/* ログアウト時のエラー表示 */}
      {displayLogoutError && <ErrorModal error={logoutError} onClickOk={() => setDisplayLogoutError(false)} />}
    </>
  );
}

export default Header;
